/**
 * Revisão da O.S. preventiva — "o que entra nesta O.S.".
 *
 * Todos os itens elegíveis chegam MARCADOS; quem revisa só desmarca o que não
 * será feito agora. Item desmarcado "não faz parte desta O.S.": não vira
 * execução, o vencimento anterior é mantido e o alerta continua. Só antes de
 * iniciar a execução — depois, o que vale é o checklist.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceWorkOrderDetailDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { fmtDay } from "./preventive-ui";
import { MaintenanceIcon } from "./MaintenanceIcons";

export function WorkOrderReviewCard({ o, podeEditar }: { o: MaintenanceWorkOrderDetailDto; podeEditar: boolean }) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const incluidos = o.planComponents ?? [];
  const excluidos = o.excludedComponents ?? [];
  const todos = [...incluidos, ...excluidos];
  const revisavel = podeEditar && (o.status === "open" || o.status === "scheduled");

  const alternar = useMutation({
    mutationFn: (v: { code: string; included: boolean }) =>
      api.patch(`/maintenance/work-orders/${o.id}/plan-components/${v.code}`, { included: v.included }),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível alterar o item."),
  });

  if (o.type !== "preventive" || !todos.length) return null;
  const comp = new Map((o.planSnapshot?.components ?? []).map((c) => [c.componentCode, c]));

  return (
    <section className="card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <MaintenanceIcon name="wrench" className="h-5 w-5 text-tm-700" /> Itens desta O.S.
        </h3>
        <span className="badge-gray">{incluidos.length} de {todos.length} selecionados</span>
      </div>
      <p className="text-sm text-gray-500">
        {revisavel
          ? "Todos os itens vieram selecionados. Desmarque o que não será feito nesta O.S. O vencimento dele é mantido."
          : "Itens previstos para esta O.S. Só o que for marcado como realizado no checklist atualiza o plano."}
      </p>
      {erro && <p className="text-sm text-danger-600">{erro}</p>}
      <ul className="grid gap-2 sm:grid-cols-2">
        {todos.map((code) => {
          const c = comp.get(code);
          const incluido = incluidos.includes(code);
          return (
            <li key={code}>
              <label
                className={`flex min-h-[4rem] items-start gap-3 rounded-xl border p-3 ${
                  incluido ? "border-tm-600/40 bg-tm-50/40" : "border-surface-border bg-surface-muted opacity-75"
                } ${revisavel ? "cursor-pointer" : ""}`}
              >
                <input
                  type="checkbox" className="mt-1 h-5 w-5" checked={incluido}
                  disabled={!revisavel || alternar.isPending}
                  onChange={(e) => alternar.mutate({ code, included: e.target.checked })}
                />
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900">{c?.label ?? code}</span>
                  <span className="block text-xs text-gray-500">
                    {incluido
                      ? [
                          c?.nextKm != null ? `Previsto ${fmtNumber(c.nextKm)} km` : null,
                          c?.nextDate ? `até ${fmtDay(c.nextDate)}` : null,
                        ].filter(Boolean).join(" · ") || "Sem previsão (sem base)"
                      : "Não faz parte desta O.S., vencimento anterior mantido"}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
